import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUp, ArrowUpRight } from 'lucide-react'
import { DomeMark } from '@/components/generals/dome-mark'
import { useLenis } from '@/components/generals/smooth-scroll'
import { contact, copyrightYear } from '@/constants/site'

const labelClass = 'mb-3 font-mono text-[11px] uppercase tracking-[0.18em] text-tone-muted'

/**
 * Sits behind the page (sticky, below the main content) so the last section
 * lifts away to reveal it, like a curtain.
 */
export const SiteFooter: FC = (): ReactNode => {
    const lenis = useLenis()

    const backToTop = () => {
        if (lenis) {
            lenis.scrollTo(0, { duration: 2.2 })
            return
        }

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' })
    }

    return (
        <footer data-tone-scope='amber' className='relative z-0 bg-tone text-tone-fg lg:sticky lg:bottom-0'>
            <div className='grid grid-cols-12 gap-x-6 gap-y-12 px-5 pt-20 md:px-10 md:pt-28'>
                <div className='col-span-12 lg:col-span-5'>
                    <p className='font-heading text-5xl font-light leading-[0.95] tracking-[-0.02em] text-balance md:text-7xl'>
                        Feeling at home <em className='font-normal'>in Berlin.</em>
                    </p>

                    <a
                        href={contact.bookingUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='group mt-10 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.18em]'
                    >
                        <span className='link-underline'>Book our venue now!</span>
                        <ArrowUpRight className='size-4 transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' aria-hidden='true' />
                    </a>
                </div>

                <div className='col-span-6 text-sm leading-relaxed sm:col-span-4 lg:col-span-2 lg:col-start-7'>
                    <p className={labelClass}>Visit</p>
                    <address className='not-italic'>
                        {contact.street}
                        <br />
                        {contact.entrance}
                        <br />
                        {contact.postcode} {contact.city}-{contact.district}
                    </address>
                    <a href={contact.mapsUrl} target='_blank' rel='noopener noreferrer' className='link-underline mt-3 inline-block'>
                        Open in Maps
                    </a>
                </div>

                <div className='col-span-6 text-sm leading-relaxed sm:col-span-4 lg:col-span-2'>
                    <p className={labelClass}>Contact</p>
                    <a href={`mailto:${contact.email}`} className='link-underline break-all'>
                        {contact.email}
                    </a>
                    <br />
                    <a href={contact.bookingUrl} target='_blank' rel='noopener noreferrer' className='link-underline'>
                        Telegram {contact.bookingHandle}
                    </a>
                </div>

                <div className='col-span-12 text-sm leading-relaxed sm:col-span-4 lg:col-span-2'>
                    <p className={labelClass}>Community</p>
                    <a href={contact.communityUrl} target='_blank' rel='noopener noreferrer' className='link-underline'>
                        Community news on Telegram
                    </a>
                    <br />
                    <Link href='/impressum' className='link-underline'>
                        Impressum
                    </Link>
                    <br />
                    <Link href='/datenschutz' className='link-underline'>
                        Datenschutz
                    </Link>
                </div>
            </div>

            <p
                aria-hidden='true'
                className='mt-16 flex select-none items-end justify-center gap-[2.2vw] overflow-hidden px-5 font-heading text-[16.4vw] font-light leading-[0.8] tracking-[-0.045em] md:mt-24 md:px-10'
            >
                <span>Unity</span>
                <DomeMark className='mb-[1.4vw] w-[8vw]' />
                <span>Space</span>
            </p>

            <div className='mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-tone-line px-5 py-5 font-mono text-[11px] uppercase tracking-[0.18em] md:px-10'>
                <span>
                    &copy; {copyrightYear} {contact.organisation}
                </span>

                <button type='button' onClick={backToTop} className='group inline-flex items-center gap-2'>
                    <span className='link-underline'>Back to top</span>
                    <ArrowUp className='size-3.5 transition-transform duration-500 group-hover:-translate-y-0.5' aria-hidden='true' />
                </button>
            </div>
        </footer>
    )
}
